import React, { useState } from 'react';
import { format, differenceInDays } from 'date-fns';
import { useCRM } from '../../context/CRMContext';
import { useAuth } from '../../context/AuthContext';
import Card from '../UI/Card';
import Button from '../UI/Button';
import Modal from '../UI/Modal';
import TaskForm from '../Forms/TaskForm';
import TaskComments from './TaskComments';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiEdit, FiTrash2, FiClock, FiCalendar, FiUser, 
  FiTag, FiMessageSquare, FiPlay, FiPause, 
  FiCheck, FiX, FiAlertTriangle, FiCheckCircle, 
  FiPaperclip, FiExternalLink 
} = FiIcons;

const TaskCard = ({ task, onEdit, onDelete, onUpdateStatus }) => {
  const { users } = useCRM();
  const { user: currentUser, isAdmin, isManager } = useAuth();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);

  const assignedUser = users?.find(u => u.id === task.assignedTo);
  const canEdit = isAdmin || isManager || currentUser.id === task.assignedTo;
  const canDelete = isAdmin || isManager;

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'todo': return 'bg-gray-100 text-gray-800';
      case 'in-progress': return 'bg-blue-100 text-blue-800';
      case 'review': return 'bg-purple-100 text-purple-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'in-progress': return FiPlay;
      case 'review': return FiPause;
      case 'completed': return FiCheck;
      case 'cancelled': return FiX;
      default: return FiClock;
    }
  };

  const getDaysUntilDue = () => {
    if (!task.dueDate) return null;
    return differenceInDays(new Date(task.dueDate), new Date());
  };

  const daysUntilDue = getDaysUntilDue();
  const isTaskActive = task.status !== 'completed' && task.status !== 'cancelled';
  const isOverdue = isTaskActive && daysUntilDue !== null && daysUntilDue < 0;
  const isDueSoon = isTaskActive && daysUntilDue !== null && daysUntilDue <= 2 && daysUntilDue >= 0;

  const handleStatusChange = (newStatus) => {
    onUpdateStatus(task.id, newStatus);
  };

  const handleEdit = (data) => {
    onEdit(task.id, data);
    setIsEditModalOpen(false);
  };

  const quickStatusButtons = [
    { status: 'todo', label: 'To Do', variant: 'outline' },
    { status: 'in-progress', label: 'In Progress', variant: 'primary' },
    { status: 'review', label: 'Review', variant: 'secondary' },
    { status: 'completed', label: 'Complete', variant: 'primary' },
  ];

  const attachments = task.attachments && Array.isArray(task.attachments) && task.attachments.length > 0 
    ? task.attachments 
    : (task.attachment_url ? [{ url: task.attachment_url, name: 'Attachment' }] : []);

  return (
    <>
      <Card className={`p-6 hover:shadow-lg transition-all ${isOverdue ? 'border-l-4 border-red-500' : isDueSoon ? 'border-l-4 border-yellow-500' : ''}`}>
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {task.title}
              {isOverdue && (
                <SafeIcon icon={FiAlertTriangle} className="h-4 w-4 text-red-500 ml-2 inline" />
              )}
            </h3>
            <div className="flex flex-wrap gap-2">
              <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(task.priority)}`}>
                {task.priority} priority
              </span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(task.status)}`}>
                <SafeIcon icon={getStatusIcon(task.status)} className="h-3 w-3 mr-1 inline" />
                {task.status.replace('-', ' ')}
              </span>
            </div>
          </div>
          {canEdit && (
            <div className="flex space-x-1">
              <button 
                onClick={() => setIsEditModalOpen(true)} 
                className="text-gray-400 hover:text-primary-600 transition-colors"
                title="Edit Task"
              >
                <SafeIcon icon={FiEdit} className="h-4 w-4" />
              </button>
              {canDelete && (
                <button 
                  onClick={() => onDelete(task.id)} 
                  className="text-gray-400 hover:text-red-600 transition-colors"
                  title="Delete Task"
                >
                  <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Task Details */}
        <div className="space-y-3 mb-4">
          <div className="flex items-center text-sm text-gray-600">
            <SafeIcon icon={FiUser} className="h-4 w-4 mr-2" />
            <span>Assigned to: {assignedUser?.fullName || assignedUser?.full_name || 'Unknown User'}</span>
          </div>
          <div className="flex items-center text-sm text-gray-600">
            <SafeIcon icon={FiCalendar} className="h-4 w-4 mr-2" />
            <span>
              Start: {format(new Date(task.startDate), 'MMM dd, yyyy')}
              {task.dueDate && (
                <>
                  {' • Due: '}
                  <span className={isOverdue ? 'text-red-600 font-medium' : isDueSoon ? 'text-yellow-600 font-medium' : ''}>
                    {format(new Date(task.dueDate), 'MMM dd, yyyy')}
                  </span>
                </>
              )}
            </span>
          </div>
          {task.estimatedHours && (
            <div className="flex items-center text-sm text-gray-600">
              <SafeIcon icon={FiClock} className="h-4 w-4 mr-2" />
              <span>Estimated: {task.estimatedHours} hours</span>
            </div>
          )}
          {task.projectId && (
            <div className="flex items-center text-sm text-gray-600">
              <SafeIcon icon={FiTag} className="h-4 w-4 mr-2" />
              <span>Project: {task.projectId}</span>
            </div>
          )}
        </div>

        {/* Description */}
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-700 line-clamp-3">{task.description}</p>
        </div>

        {/* Attachments */}
        {attachments.length > 0 && (
          <div className="mb-4">
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Attachments</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {attachments.map((file, index) => (
                <a 
                  key={index} 
                  href={file.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 truncate"
                  title={file.name}
                >
                  <SafeIcon icon={FiPaperclip} className="h-4 w-4 mr-2 text-gray-500 flex-shrink-0" />
                  <span className="truncate flex-1">{file.name || 'Attachment'}</span>
                  <SafeIcon icon={FiExternalLink} className="h-3 w-3 ml-2 text-gray-400 flex-shrink-0" />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Completed Info - Now with Exact Time */}
        {task.status === 'completed' && (
          <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200 animate-fade-in">
            <div className="flex items-center text-sm text-green-800 font-bold">
              <SafeIcon icon={FiCheckCircle} className="h-5 w-5 mr-2 text-green-600" />
              <span>Completed on: {format(new Date(task.updatedAt), 'MMM dd, yyyy @ HH:mm')}</span>
            </div>
          </div>
        )}

        {/* Due Date Warning */}
        {(isOverdue || isDueSoon) && (
          <div className={`mb-4 p-2 rounded-lg ${isOverdue ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'}`}>
            <p className={`text-sm font-medium ${isOverdue ? 'text-red-800' : 'text-yellow-800'}`}>
              {isOverdue ? `Overdue by ${Math.abs(daysUntilDue)} days` : `Due in ${daysUntilDue} days`}
            </p>
          </div>
        )}

        {/* Quick Status Update */}
        {canEdit && task.status !== 'completed' && task.status !== 'cancelled' && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm font-medium text-blue-900 mb-2">Quick Status Update:</p>
            <div className="flex flex-wrap gap-2">
              {quickStatusButtons
                .filter(btn => btn.status !== task.status)
                .map(btn => (
                  <Button 
                    key={btn.status} 
                    size="sm" 
                    variant={btn.variant}
                    onClick={() => handleStatusChange(btn.status)}
                  >
                    {btn.label}
                  </Button>
                ))}
            </div>
          </div>
        )}

        {/* Comments Section */}
        <div className="border-t border-gray-200 pt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center text-sm text-gray-600">
              <SafeIcon icon={FiMessageSquare} className="h-4 w-4 mr-2" />
              <span>Comments</span>
            </div>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => setIsCommentsOpen(!isCommentsOpen)}
            >
              {isCommentsOpen ? 'Hide' : 'Show'} Comments
            </Button>
          </div>
          {isCommentsOpen && (
            <TaskComments taskId={task.id} />
          )}
        </div>

        {/* Footer - Now with Created Time */}
        <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center text-xs text-gray-500">
          <span className="flex items-center">
            <SafeIcon icon={FiClock} className="h-3 w-3 mr-1" />
            Created: {format(new Date(task.createdAt), 'MMM dd, yyyy HH:mm')}
          </span>
          {task.updatedAt && task.updatedAt !== task.createdAt && task.status !== 'completed' && (
            <span>Updated: {format(new Date(task.updatedAt), 'MMM dd, HH:mm')}</span>
          )}
        </div>
      </Card>

      {/* Edit Modal */}
      <Modal 
        isOpen={isEditModalOpen} 
        onClose={() => setIsEditModalOpen(false)} 
        title="Edit Task" 
        size="xl"
      >
        <TaskForm 
          task={task} 
          onSubmit={handleEdit} 
          onCancel={() => setIsEditModalOpen(false)} 
        />
      </Modal>
    </>
  );
};

export default TaskCard;